// 読み上げの文面を文単位の短いチャンクに分ける（#1966）。
//
// 面接モードの自由生成の質問・深掘り質問は全文を一息で生成すると数秒待たされる。
// 文単位に分けて順に生成すれば、最初のチャンクが届いた時点で読み上げを始められる。
// 生成時間は文字数にほぼ比例し、固定の待ちが 1 秒強ある（Issue #1966 の実測）ため、
// 短すぎる断片は前の文にまとめ、長すぎる文は読点で分けて 1 チャンクの長さを揃える。

/** 1 チャンクの上限文字数。この長さで生成が 3 秒程度に収まり、再生の裏で次を用意できる */
export const DEFAULT_MAX_CHUNK_CHARS = 40;
/** この文字数に満たない文は前の文にまとめる。短い相槌だけを 1 回の生成にしない */
export const DEFAULT_MIN_CHUNK_CHARS = 8;

const SENTENCE_END = /[。！？!?\n]/;
const CLAUSE_END = /[、，,]/;
// 文末記号の直後に続く閉じ括弧・閉じ引用符。記号だけの断片を作らないよう前の文に含める
const CLOSING_MARK = /[」』）)】\]〕”’"']/;

interface SplitOptions {
  maxChars?: number;
  minChars?: number;
}

// 文末記号（と改行）の直後で区切る。記号と、その後に続く閉じ括弧は前の文に含める。
function splitSentences(text: string): string[] {
  const sentences: string[] = [];
  let current = "";
  let atSentenceEnd = false;
  for (const char of text) {
    if (atSentenceEnd && !CLOSING_MARK.test(char)) {
      sentences.push(current);
      current = "";
      atSentenceEnd = false;
    }
    current += char;
    if (SENTENCE_END.test(char)) atSentenceEnd = true;
  }
  if (current) sentences.push(current);
  return sentences.map((s) => s.trim()).filter((s) => s.length > 0);
}

// 上限を超える文を読点で分ける。読点までの節を上限に収まるだけまとめ、読点が無ければそのまま返す。
function splitLongSentence(sentence: string, maxChars: number): string[] {
  if (sentence.length <= maxChars) return [sentence];

  const clauses: string[] = [];
  let current = "";
  for (const char of sentence) {
    current += char;
    if (CLAUSE_END.test(char)) {
      clauses.push(current);
      current = "";
    }
  }
  if (current) clauses.push(current);
  if (clauses.length <= 1) return [sentence];

  const pieces: string[] = [];
  let buffer = "";
  for (const clause of clauses) {
    if (buffer && buffer.length + clause.length > maxChars) {
      pieces.push(buffer);
      buffer = clause;
    } else {
      buffer += clause;
    }
  }
  if (buffer) pieces.push(buffer);
  return pieces;
}

/**
 * 文面をチャンクの配列に分ける。空文字・空白のみは空配列を返す。
 * 区切りが無い短い文面はそのまま 1 要素で返す。
 */
export function splitTtsChunks(
  text: string,
  { maxChars = DEFAULT_MAX_CHUNK_CHARS, minChars = DEFAULT_MIN_CHUNK_CHARS }: SplitOptions = {},
): string[] {
  const trimmed = text.trim();
  if (!trimmed) return [];

  const chunks: string[] = [];
  for (const sentence of splitSentences(trimmed)) {
    for (const piece of splitLongSentence(sentence, maxChars)) {
      const previous = chunks[chunks.length - 1];
      // 短い断片は前の文にまとめる。まとめた結果が上限を超えるなら独立させる
      if (previous !== undefined && piece.length < minChars && previous.length + piece.length <= maxChars) {
        chunks[chunks.length - 1] = previous + piece;
      } else {
        chunks.push(piece);
      }
    }
  }

  // 先頭が短い断片のまま残ったら次の文にまとめる（前が無いため後ろへ寄せる）
  if (chunks.length >= 2 && chunks[0].length < minChars && chunks[0].length + chunks[1].length <= maxChars) {
    chunks.splice(0, 2, chunks[0] + chunks[1]);
  }
  return chunks;
}
